import React, { useId } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import ChartDefs from './ChartDefs';
import ChartTooltip from './ChartTooltip';
import useChartTheme from './useChartTheme';
import { makeChartIds } from './chartTokens';

/* ──────────────────────────────────────────────────────────────────────────
   GlowBars — bar recipe (§3): rounded bars, translucent warm gradient fill,
   glowing coloured outline. `cellTone(entry) → 'gold' | 'pos' | 'neg'` lets a
   tab carry a directional cue (e.g. a fully-paid debt glows green) via the
   outline colour while the gold gradient fill stays consistent.
   ────────────────────────────────────────────────────────────────────────── */

const GlowBars = ({
  data,
  dataKey,
  xKey,
  height = 300,
  yTickFormatter,
  valueFormatter,
  xAxisProps = {},
  cellTone,               // optional (entry) => tone
}) => {
  const ids = makeChartIds(useId());
  const colors = useChartTheme();
  const toneOf = (entry) => (cellTone ? cellTone(entry) : 'gold');

  return (
    <>
      <ChartDefs ids={ids} colors={colors} />
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={colors.line} />
          <XAxis
            dataKey={xKey}
            tick={{ fill: colors.ink3, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            {...xAxisProps}
          />
          <YAxis
            tick={{ fill: colors.ink3, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={48}
            tickFormatter={yTickFormatter}
          />
          <Tooltip
            cursor={{ fill: colors.accent, fillOpacity: 0.06 }}
            content={
              <ChartTooltip
                valueFormatter={valueFormatter}
                getColor={(p) => colors.tone[toneOf(p.payload)]}
              />
            }
          />
          <Bar
            dataKey={dataKey}
            radius={[6, 6, 0, 0]}
            fill={`url(#${ids.bar})`}
            stroke={colors.tone.gold}
            strokeWidth={1.5}
            filter={`url(#${ids.glow})`}
          >
            {data.map((entry, i) => (
              <Cell key={i} stroke={colors.tone[toneOf(entry)]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default GlowBars;
